import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Unit } from '../types/unit';

interface FavoriteUnit {
  unitId: string;
  timestamp: number;
  unitSnapshot: {
    id: string;
    title: string;
    address: string;
    price: number;
    priceUnit: 'month' | 'year';
    bedrooms: number;
    bathrooms: number;
    images: string[];
    listed: boolean;
    propertyName?: string;
    unitNumber?: string;
    soleResidentialUnit?: boolean;
    availableDate: string;
    netLeasableArea?: number | null;
    furnished?: boolean;
    petFriendly?: boolean;
  };
}

interface FavoritesContextType {
  favoriteIds: Set<string>;
  favoriteUnits: FavoriteUnit[];
  toggleFavorite: (unit: Unit) => void;
  isFavorite: (unitId: string) => boolean;
  clearFavorites: () => void;
  getFavoriteCount: () => number;
  removeFavorite: (unitId: string) => void;
}

const FavoritesContext = createContext<FavoritesContextType | null>(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: React.ReactNode;
}

const FAVORITES_STORAGE_KEY = 'amber-peak-favorites';
const MAX_FAVORITES = 50; // Reasonable limit to prevent localStorage bloat

function readStoredFavorites(): FavoriteUnit[] {
  try {
    const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!storedFavorites) return [];
    const parsed: FavoriteUnit[] = JSON.parse(storedFavorites);
    const validFavorites = parsed.filter(
      (fav) => fav.unitId && fav.timestamp && fav.unitSnapshot && fav.unitSnapshot.id,
    );
    validFavorites.sort((a, b) => b.timestamp - a.timestamp);
    return validFavorites.slice(0, MAX_FAVORITES);
  } catch {
    return [];
  }
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favoriteUnits, setFavoriteUnits] = useState<FavoriteUnit[]>(readStoredFavorites);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(
    () => new Set(readStoredFavorites().map((favorite) => favorite.unitId)),
  );

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (storedFavorites) {
        const parsed: FavoriteUnit[] = JSON.parse(storedFavorites);
        
        // Validate and clean up stored data
        const validFavorites = parsed.filter(fav => 
          fav.unitId && 
          fav.timestamp && 
          fav.unitSnapshot &&
          fav.unitSnapshot.id
        );

        // Sort by timestamp (most recent first)
        validFavorites.sort((a, b) => b.timestamp - a.timestamp);
        
        // Limit to MAX_FAVORITES
        const limitedFavorites = validFavorites.slice(0, MAX_FAVORITES);
        
        setFavoriteUnits(limitedFavorites);
        setFavoriteIds(new Set(limitedFavorites.map(fav => fav.unitId)));

        // If we had to clean up, save the cleaned version
        if (limitedFavorites.length !== parsed.length) {
          localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(limitedFavorites));
        }
      }
    } catch (error) {
      console.error('Failed to load favorites from localStorage:', error);
      // Clear corrupted data
      localStorage.removeItem(FAVORITES_STORAGE_KEY);
    }
  }, []);

  // Save favorites to localStorage whenever they change
  const saveFavoritesToStorage = useCallback((favorites: FavoriteUnit[]) => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Failed to save favorites to localStorage:', error);
      
      // If storage is full, try to make space by removing oldest favorites
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        const reducedFavorites = favorites.slice(0, Math.floor(favorites.length * 0.8));
        try {
          localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(reducedFavorites));
          setFavoriteUnits(reducedFavorites);
          setFavoriteIds(new Set(reducedFavorites.map(fav => fav.unitId)));
        } catch (secondError) {
          console.error('Failed to save reduced favorites:', secondError);
        }
      }
    }
  }, []);

  const toggleFavorite = useCallback((unit: Unit) => {
    const unitId = unit.id;
    
    setFavoriteUnits(prevFavorites => {
      const existingIndex = prevFavorites.findIndex(fav => fav.unitId === unitId);
      
      let newFavorites: FavoriteUnit[];
      
      if (existingIndex >= 0) {
        // Remove from favorites
        newFavorites = prevFavorites.filter((_, index) => index !== existingIndex);
      } else if (unit.listed !== true) {
        return prevFavorites;
      } else {
        // Add to favorites (check limit)
        if (prevFavorites.length >= MAX_FAVORITES) {
          // Remove oldest favorite to make room
          newFavorites = prevFavorites.slice(0, MAX_FAVORITES - 1);
        } else {
          newFavorites = [...prevFavorites];
        }
        
        // Create unit snapshot with essential data
        const unitSnapshot = {
          id: unit.id,
          title: unit.title,
          address: unit.address,
          price: unit.price,
          priceUnit: unit.priceUnit,
          bedrooms: unit.bedrooms,
          bathrooms: unit.bathrooms,
          images: unit.images || [],
          listed: unit.listed || false,
          propertyName: unit.propertyName,
          unitNumber: unit.unitNumber,
          soleResidentialUnit: unit.soleResidentialUnit,
          availableDate: unit.availableDate,
          netLeasableArea: unit.netLeasableArea,
          furnished: unit.furnished,
          petFriendly: unit.petFriendly,
        };
        
        // Add new favorite at the beginning (most recent)
        newFavorites.unshift({
          unitId,
          timestamp: Date.now(),
          unitSnapshot,
        });
      }
      
      // Update the Set of IDs
      setFavoriteIds(new Set(newFavorites.map(fav => fav.unitId)));
      
      // Save to localStorage
      saveFavoritesToStorage(newFavorites);
      
      return newFavorites;
    });
  }, [saveFavoritesToStorage]);

  const isFavorite = useCallback((unitId: string) => {
    return favoriteIds.has(unitId);
  }, [favoriteIds]);

  const removeFavorite = useCallback((unitId: string) => {
    setFavoriteUnits(prevFavorites => {
      const newFavorites = prevFavorites.filter(fav => fav.unitId !== unitId);
      setFavoriteIds(new Set(newFavorites.map(fav => fav.unitId)));
      saveFavoritesToStorage(newFavorites);
      return newFavorites;
    });
  }, [saveFavoritesToStorage]);

  const clearFavorites = useCallback(() => {
    setFavoriteUnits([]);
    setFavoriteIds(new Set());
    localStorage.removeItem(FAVORITES_STORAGE_KEY);
  }, []);

  const getFavoriteCount = useCallback(() => {
    return favoriteUnits.length;
  }, [favoriteUnits.length]);

  const contextValue: FavoritesContextType = {
    favoriteIds,
    favoriteUnits,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    getFavoriteCount,
    removeFavorite,
  };

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};
